import axios from "axios";
import api from "./api";
import {
  BaseUsuario,
  User,
  ValidadeUser,
  ResponseValidateUser,
  UsuarioLotacao,
  UsuarioInstituicao,
} from "../types/Models";

export async function fetchUsuarios(
  currentPage: number,
  perPage: number,
  user: User,
  nomeFilter?: string,
  userTypeFilter: string = "",
  statusFilter?: number,
  ambitoFilter?: number,
  municipalityFilter?: number,
  lotacaoFilter?: number,
  cpfFilter?: string,
  emailFilter?: string,
  roleFilter?: number,
  search?: string,
  accessFilter?: string,
  orderBy: string = "nome",
  orderDirection: "asc" | "desc" = "asc",
): Promise<BaseUsuario[]> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/all`;

  try {
    const token = sessionStorage.getItem("authToken");

    if (!token) {
      throw new Error("Token não encontrado");
    }

    const response = await api.get(apiUrl, {
      params: {
        page: currentPage,
        perPage,
        nomeFilter,
        userTypeFilter,
        statusFilter,
        ambitoFilter,
        municipalityFilter,
        lotacaoFilter,
        cpfFilter,
        emailFilter,
        role: user.role,
        roleFilter,
        search: search?.trim() || undefined,
        access_filter: accessFilter || undefined,
        order_by: orderBy,
        order_direction: orderDirection,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const result = response.data;
    const users = result.users;

    return users.map((item: any) => ({
      id: item.id.toString(),
      nome: item.nome,
      possui_nome_social:
        item.possui_nome_social === true ||
        item.possui_nome_social === 1 ||
        item.possui_nome_social === "1" ||
        item.possui_nome_social === "true",
      nome_social: item.nome_social ?? "",
      email: item.email,
      role: item.role, // Corrigido para pegar o nome da role
      ativo: Boolean(item.ativo), // Certifica que "ativo" retorna booleano
      cpf: item.cpf,
      ambito: item.dados_profissionais?.ambito_nome || "",
      municipio: item.dados_profissionais?.municipio_nome || "",
      lotacao:
        item.dados_profissionais?.lotacao_nome ||
        item.dados_profissionais?.orgao_nome ||
        "",
      last_login_at: item.last_login_at ?? null,
      last_access_at: item.last_access_at ?? null,
      created_at: item.created_at ?? null,
    }));
  } catch (error) {
    //console.error("Erro ao buscar usuários:", error);
    throw new Error(
      error instanceof Error
        ? error.message
        : "Erro desconhecido ao buscar usuários",
    );
  }
}

export async function countUsuarios(
  user: User,
  nomeFilter?: string,
  userTypeFilter: string = "",
  statusFilter?: number,
  ambitoFilter?: number,
  municipalityFilter?: number,
  lotacaoFilter?: number,
  cpfFilter?: string,
  emailFilter?: string,
  roleFilter?: number,
  search?: string,
  accessFilter?: string,
  orderBy: string = "nome",
  orderDirection: "asc" | "desc" = "asc",
): Promise<number> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/all`;

  try {
    const token = sessionStorage.getItem("authToken");
    if (!token) {
      throw new Error("Token não encontrado");
    }

    const response = await api.get(apiUrl, {
      params: {
        nomeFilter,
        userTypeFilter,
        statusFilter,
        ambitoFilter,
        municipalityFilter,
        lotacaoFilter,
        cpfFilter,
        emailFilter,
        role: user.role,
        roleFilter,
        countUsers: true,
        search: search?.trim() || undefined,
        access_filter: accessFilter || undefined,
        order_by: orderBy,
        order_direction: orderDirection,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data.total_users;
  } catch (error) {
    console.error("Erro ao contar usuários:", error);
    throw new Error("Erro ao contar usuários");
  }
}

export interface UsuariosTotalizadores {
  usuarios_cadastrados: number;
  usuarios_ativos: number;
  usuarios_inativos: number;
  responsaveis_tecnicos: number;
  usuarios: number;
  colaboradores_externos: number;
  administradores: number;
  ambito_estadual: number;
  ambito_municipal: number;
  acessos_hoje: number;
  acessos_ultimos_7_dias: number;
  acessos_ultimos_30_dias: number;
  nunca_acessou: number;
}

export type UsuarioInscricao = {
  id: number;
  inscrito_em: string;
  ambito_nome?: string | null;
  municipio_nome?: string | null;
  turma?: {
    nome?: string;
    descricao_modalidade?: string;
    data_inicio?: string;
    data_fim?: string;
  };
  acao?: {
    nome?: string;
    tipologia?: string;
  };
  resultado?: {
    status?: string;
    percentual_presenca?: number | null;
    nota?: number | null;
  } | null;
};

export async function findUsuarioInscricoes(
  userId: string | number,
): Promise<UsuarioInscricao[]> {
  const token = sessionStorage.getItem("authToken");

  if (!token) {
    throw new Error("Token não encontrado");
  }

  const response = await api.get("/usuario/inscricoes", {
    params: {
      user_id: userId,
      perPage: 999999,
    },
    headers: {
      Authorization: "Bearer " + token,
    },
  });

  return response.data?.data?.inscricoes || [];
}

export async function fetchUsuariosTotalizadores(
  user: User,
  nomeFilter?: string,
  userTypeFilter: string = "",
  statusFilter?: number,
  ambitoFilter?: number,
  municipalityFilter?: number,
  lotacaoFilter?: number,
  cpfFilter?: string,
  emailFilter?: string,
  roleFilter?: number,
  search?: string,
  accessFilter?: string,
  orderBy: string = "nome",
  orderDirection: "asc" | "desc" = "asc",
): Promise<UsuariosTotalizadores> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/totalizadores`;
  const token = sessionStorage.getItem("authToken");

  if (!token) {
    throw new Error("Token não encontrado");
  }

  const response = await api.get(apiUrl, {
    params: {
      nomeFilter,
      userTypeFilter,
      statusFilter,
      ambitoFilter,
      municipalityFilter,
      lotacaoFilter,
      cpfFilter,
      emailFilter,
      role: user.role,
      roleFilter,
      search: search?.trim() || undefined,
      access_filter: accessFilter || undefined,
      order_by: orderBy,
      order_direction: orderDirection,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data?.data || {};
}

export async function deleteUser(userId: string | number): Promise<string> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/${userId}`;

  try {
    const token = sessionStorage.getItem("authToken");

    if (!token) {
      throw new Error("Token não encontrado");
    }

    const response = await api.delete(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data?.message || "Usuario excluido com sucesso.";
  } catch (error) {
    throw new Error(`Erro ao deletar usuário: ${error}`);
  }
}

export async function updateUserInfo(
  user: User,
  userId: string,
  updatedInfo: BaseUsuario,
): Promise<void> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/basic/${userId}`;

  try {
    const token = sessionStorage.getItem("authToken");

    if (!token) {
      throw new Error("Token não encontrado");
    }

    await api.put(
      apiUrl,
      {
        nome: updatedInfo.nome,
        ativo: updatedInfo.ativo,
        role: updatedInfo.role,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
  } catch (error) {
    throw new Error(`Erro ao atualizar usuário: ${error}`);
  }
}

export async function sendTemporaryPassword(
  userId: string | number,
): Promise<void> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/newpassword/${userId}`;
  try {
    const token = sessionStorage.getItem("authToken");

    if (!token) {
      throw new Error("Token não encontrado");
    }

    await axios.post(apiUrl, null, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    throw new Error(`Erro ao enviar senha temporária: ${error}`);
  }
}

export async function updateUserComplete(userId: number, payloadUpdate: any) {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/${userId}`;

  try {
    const token = sessionStorage.getItem("authToken");

    if (!token) {
      throw new Error("Token não encontrado");
    }

    const response = await axios.put(apiUrl, payloadUpdate, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      return response.data.message;
    } else {
      return "Houve um erro ao alterar os dados. Por favor, tente novamente.";
    }
  } catch (error) {
    return "Erro ao atualizar usuário";
  }
}

export async function inactivateUser(userId: string | number): Promise<void> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/inactivate/${userId}`;
  try {
    const token = sessionStorage.getItem("authToken");

    if (!token) {
      throw new Error("Token não encontrado");
    }

    await api.delete(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    throw new Error(`Erro ao inativar usuário: ${error}`);
  }
}

export async function activateUser(userId: string | number): Promise<void> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/activate/${userId}`;
  try {
    const token = sessionStorage.getItem("authToken");

    if (!token) {
      throw new Error("Token não encontrado");
    }

    await axios.patch(
      apiUrl,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  } catch (error) {
    throw new Error(`Erro ao ativar usuário: ${error}`);
  }
}

export async function inactivateUsers(usersIds: number[]): Promise<void> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/inactivate-multiple`;
  try {
    const token = sessionStorage.getItem("authToken");

    if (!token) {
      throw new Error("Token não encontrado");
    }

    await axios.post(
      apiUrl,
      {
        ids: usersIds,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  } catch (error) {
    throw new Error(`Erro ao inativar usuários: ${error}`);
  }
}

export async function activateUsers(usersIds: number[]): Promise<void> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/activate-multiple`;
  try {
    const token = sessionStorage.getItem("authToken");

    if (!token) {
      throw new Error("Token não encontrado");
    }

    await axios.post(
      apiUrl,
      {
        ids: usersIds,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  } catch (error) {
    throw new Error(`Erro ao reativar usuários: ${error}`);
  }
}

export async function newpasswordUsers(usersIds: number[]): Promise<void> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/newpassword-multiple`;
  try {
    const token = sessionStorage.getItem("authToken");

    if (!token) {
      throw new Error("Token não encontrado");
    }

    await axios.post(
      apiUrl,
      {
        ids: usersIds,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  } catch (error) {
    throw new Error(
      `Erro ao reenviar senhas temporárias para os usuários: ${error}`,
    );
  }
}

export async function validateExistsUsers(
  data: ValidadeUser,
): Promise<ResponseValidateUser> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/validate-exists`;
  try {
    const response = await axios.post(apiUrl, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    throw new Error(`Erro ao validar existência do usuário: ${error}`);
  }
}

export async function findAllUsersLotacao(
  lotacao_id?: number,
): Promise<UsuarioLotacao[]> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/lotacoes`;

  try {
    const token = sessionStorage.getItem("authToken");

    if (!token) {
      throw new Error("Token não encontrado");
    }

    const response = await api.get(apiUrl, {
      params: {
        lotacao_id,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const result = response.data;

    return result;
  } catch (error) {
    console.error("Erro ao buscar usuários:", error);
    throw new Error(
      error instanceof Error
        ? error.message
        : "Erro desconhecido ao buscar usuários",
    );
  }
}

export async function findAllUsersInstituicao(
  institution_id?: number,
): Promise<UsuarioInstituicao[]> {
  const apiUrl = `${process.env.REACT_APP_API_URL}/usuario/instituicoes`;

  try {
    const token = sessionStorage.getItem("authToken");

    if (!token) {
      throw new Error("Token não encontrado");
    }

    const response = await api.get(apiUrl, {
      params: {
        institution_id,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const result = response.data;

    return result;
  } catch (error) {
    console.error("Erro ao buscar usuários:", error);
    throw new Error(
      error instanceof Error
        ? error.message
        : "Erro desconhecido ao buscar usuários",
    );
  }
}
